// Questionnaire JSON importer (experimental, dev-only).
//
// Ingests a canonical questionnaire payload (the export schema from
// SPEC_questionnaire_json_export.md) and creates a brand-new questionnaire
// through the app's own validated endpoints — never raw SQL.
//
// Design (see SPEC_questionnaire_json_importer.md):
//   * Additive and non-destructive. It only CREATES. It never overwrites.
//   * Collision guard: if a questionnaire with the target slug/name already
//     exists, the caller is told to delete it first (we don't touch it).
//   * Best-effort, not transactional: many endpoint calls can't be atomic.
//     On any mid-import failure we delete the just-created questionnaire
//     (ON DELETE CASCADE removes its sections + junction items), so the
//     only residue is orphaned bank questions/cards — acceptable in dev.
//   * Sections are created per-questionnaire (we don't reach into the
//     global section pool here).

import apiService from '@/services/apiService';
import { saveCard } from '@/services/cardApi';

// ── small helpers ───────────────────────────────────────────────────

const LOCALES = ['de', 'en', 'fr', 'it'];

// Turn a free-text name into a slug matching ^[a-z][a-z0-9_-]{1,63}$.
export function slugify(text) {
  const base = String(text || '')
    .toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  // Must start with a letter.
  const s = /^[a-z]/.test(base) ? base : `q-${base}`;
  return s.slice(0, 64) || 'questionnaire';
}

// Strip null/empty locale values so the backend receives a clean sparse
// i18n dict (its native shape). Returns {} for an all-empty field.
function compactI18n(dict) {
  const out = {};
  if (dict && typeof dict === 'object') {
    for (const loc of LOCALES) {
      const v = dict[loc];
      if (typeof v === 'string' && v.trim()) out[loc] = v;
    }
  }
  return out;
}

// True if an object's keys are all locale codes (an i18n leaf).
function isI18n(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  const keys = Object.keys(obj);
  return keys.length > 0 && keys.every(k => LOCALES.includes(k));
}

// Recursively compact every i18n leaf in a content tree, leaving structure,
// ids, scores, and numbers untouched.
function compactContentTree(value) {
  if (isI18n(value)) return compactI18n(value);
  if (Array.isArray(value)) return value.map(compactContentTree);
  if (value && typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) out[k] = compactContentTree(value[k]);
    return out;
  }
  return value;
}

function firstI18n(dict) {
  if (!dict || typeof dict !== 'object') return '';
  for (const loc of ['en', ...LOCALES]) {
    const v = dict[loc];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

const VALID_CODE = /^[A-Z][A-Z0-9_]{0,31}$/;
const VALID_SLUG = /^[a-z][a-z0-9_-]{1,63}$/;

// Mint a valid question code when the payload doesn't carry one.
function mintCode(sectionIdx, itemIdx) {
  return `IMP_${sectionIdx + 1}_${itemIdx + 1}`.slice(0, 32);
}

// Rebuild content-card blocks from the export's `fields` map
// ({ title, body_1, ... } → [{ kind: 'TITLE', text }, ...]). Images are
// not carried by the export, so imported cards have no image blocks.
function blocksFromFields(fields) {
  const blocks = [];
  if (fields && typeof fields === 'object') {
    for (const key of Object.keys(fields)) {
      const text = compactI18n(fields[key]);
      if (Object.keys(text).length === 0) continue;
      blocks.push({ kind: key.toUpperCase(), text });
    }
  }
  return blocks;
}

// ── validation ──────────────────────────────────────────────────────

// Keep in step with QuestionType in backend/src/routers/seq_questions.py and
// with the seq_questions_type_check CHECK constraint. This list had fallen
// behind: short_text and number were live types that an import still rejected.
const QUESTION_TYPES = [
  'numeric_stepper', 'single_choice', 'matrix', 'short_text', 'number',
  'numeric_grid',
];

// Lightweight structural validation. Returns an array of human-readable
// problems (empty = ok). The backend's per-entity validators are the real
// gate; this just catches obvious shape errors before we create anything.
export function validateImportPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return ['Payload is not a JSON object.'];
  }
  if (!Array.isArray(payload.sections)) {
    errors.push('Missing "sections" array.');
    return errors;
  }
  if (payload.sections.length === 0) {
    errors.push('"sections" is empty — nothing to import.');
  }
  payload.sections.forEach((sec, si) => {
    const where = `section ${si + 1}`;
    if (!isI18n(sec.name) || firstI18n(sec.name) === '') {
      errors.push(`${where}: missing a name in any locale.`);
    }
    const items = Array.isArray(sec.items) ? sec.items : [];
    items.forEach((it, ii) => {
      const w = `${where} item ${ii + 1}`;
      if (!it || typeof it !== 'object') { errors.push(`${w}: not an object.`); return; }
      if (it.item_type === 'question') {
        if (!QUESTION_TYPES.includes(it.question_type)) {
          errors.push(`${w}: unknown question_type "${it.question_type}".`);
        }
        if (!it.content || typeof it.content !== 'object' || firstI18n(it.content.title) === '') {
          errors.push(`${w}: question is missing content.title.`);
        }
      } else if (it.item_type === 'content_card') {
        if (!it.fields || typeof it.fields !== 'object') {
          errors.push(`${w}: content_card is missing "fields".`);
        }
      } else if (it.item_type === 'flow_card') {
        if (!it.code) errors.push(`${w}: flow_card is missing "code".`);
      } else {
        errors.push(`${w}: unknown item_type "${it.item_type}".`);
      }
    });
  });
  return errors;
}

// ── collision check ─────────────────────────────────────────────────

// Find an existing questionnaire that the import would collide with
// (same slug, or same name). Returns the row or null. Also returns the
// derived slug we'd use, for the caller's report.
export async function findCollision(name) {
  const slug = slugify(name);
  const all = await apiService.fetchQuestionnaires();
  const list = Array.isArray(all) ? all : [];
  const lowerName = String(name || '').trim().toLowerCase();
  const existing = list.find(q =>
    q.slug === slug
    || (q.name && String(q.name).trim().toLowerCase() === lowerName));
  return { slug, existing: existing || null };
}

// ── the import ──────────────────────────────────────────────────────

export class ImportError extends Error {}

// Create the questionnaire and everything under it. `name` (from the UI
// text box) is authoritative. Returns { ok, slug, counts } on success, or
// { ok: false, error, rolledBack } on failure (after deleting the partial
// questionnaire). Assumes the caller already ran findCollision and the
// ground is clear.
export async function importQuestionnaire(payload, name) {
  const slug = slugify(name);
  const counts = { sections: 0, questions: 0, content_cards: 0, flow_cards: 0 };
  // Human-readable description of the operation in flight, surfaced in the
  // failure report so a 500 (whose body is generic) is still diagnosable.
  let step = `create questionnaire "${name}" (slug: ${slug})`;
  let lastPayload = { name: String(name).trim(), slug, status: 'draft' };

  const questionnaire = await apiService.createQuestionnaire(lastPayload);

  try {
    const flowCards = await apiService.fetchFlowCards();
    const flowIdByCode = new Map((flowCards || []).map(f => [f.code, f.id]));

    for (const [si, section] of payload.sections.entries()) {
      const secSlug = (VALID_SLUG.test(section.slug || '') && section.slug)
        || slugify(firstI18n(section.name) || `section-${si + 1}`);
      const sectionBody = { slug: secSlug, name: compactI18n(section.name) };
      step = `create section ${si + 1} "${firstI18n(section.name)}" (slug: ${secSlug})`;
      lastPayload = sectionBody;
      // Sections are per-questionnaire: create a fresh one (no reuse).
      // position is assigned MAX+1, so creating in payload order yields
      // the intended section order with no separate reorder step.
      const createdSection = await apiService.createSectionForQuestionnaire(slug, sectionBody);
      const sectionId = createdSection.id;
      counts.sections += 1;

      const items = Array.isArray(section.items) ? section.items : [];
      for (const [ii, item] of items.entries()) {
        let itemType = item.item_type;
        let itemId;
        const where = `section ${si + 1} item ${ii + 1} (${item.item_type})`;

        if (item.item_type === 'question') {
          const code = (VALID_CODE.test(item.code || '') && item.code) || mintCode(si, ii);
          const qBody = {
            code,
            question_type: item.question_type,
            content: compactContentTree(item.content || {}),
            help_text: compactI18n(item.help_text),
            section_id: sectionId,
          };
          step = `${where}: create question "${code}" (${item.question_type})`;
          lastPayload = qBody;
          const created = await apiService.createQuestion(qBody);
          itemId = created.id;
          counts.questions += 1;
        } else if (item.item_type === 'content_card') {
          const cardBody = {
            type: item.sub_type || 'factoid',
            blocks: blocksFromFields(item.fields),
            status: 'APPROVED',
          };
          step = `${where}: create content card (${cardBody.type})`;
          lastPayload = cardBody;
          const created = await saveCard(cardBody);
          itemId = created.id;
          counts.content_cards += 1;
        } else if (item.item_type === 'flow_card') {
          itemId = flowIdByCode.get(item.code);
          if (itemId == null) {
            throw new ImportError(
              `Flow card code "${item.code}" is not in the catalogue (seq_flow_cards). `
              + 'Seed it first, or remove it from the payload.',
            );
          }
          counts.flow_cards += 1;
        } else {
          continue; // unknown type — validation already flagged it
        }

        step = `${where}: attach to questionnaire`;
        await apiService.addItemToQuestionnaire(slug, {
          section_id: sectionId,
          item_type: itemType,
          item_id: itemId,
        });
      }
    }

    return { ok: true, slug, counts, questionnaire };
  } catch (error) {
    // Roll back the just-created questionnaire. ON DELETE CASCADE drops
    // its sections + junction items; orphaned bank rows are left behind.
    let rolledBack = true;
    try {
      await apiService.deleteQuestionnaire(questionnaire.id);
    } catch (e) {
      rolledBack = false;
    }
    const status = error && error.response && error.response.status;
    return { ok: false, error, status, step, lastPayload, rolledBack, slug };
  }
}
